/**
 * Schichten-Verwaltung - Vereinte Übersichtsseite
 * 
 * Kombiniert auf einer Seite:
 * - Aktuelle Schicht Status + Vorschau-Tool (oben)
 * - Schichttypen-Liste (Section)
 * - Schichtregeln-Liste (Section)
 * 
 * Keine Tabs, keine Codes im UI, zentriertes Layout.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, Plus, RefreshCw, ChevronRight, CheckCircle, XCircle, Activity, AlertTriangle, Info, Search, Shield, Layers, Trophy } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { matchShiftRule, ShiftRuleInput } from '@wash-ops/shared';
import { formatBerlinDateISO } from '../../utils/berlinDate';
import { getRuleRole, formatRoleLabel, formatValidity } from '../../utils/shiftRuleRole';

// =============================================================================
// Types
// =============================================================================

interface ShiftType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface ShiftRule {
  id: string;
  code: string;
  name: string;
  shiftType: { id: string; code: string; name: string };
  weekdays: number[];
  startTime: string;
  endTime: string;
  validFrom: string | null;
  validUntil: string | null;
  priority: number;
  isActive: boolean;
}

interface CurrentShiftResponse {
  hasActiveShift: boolean;
  ruleName: string | null;
  shiftTypeName: string | null;
  shiftDateStr: string | null;
  startTime: string | null;
  endTime: string | null;
}

// =============================================================================
// Helpers
// =============================================================================

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa', 7: 'So',
};

const WEEKDAY_FULL: Record<number, string> = {
  1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch', 4: 'Donnerstag', 5: 'Freitag', 6: 'Samstag', 7: 'Sonntag',
};

function formatWeekdays(weekdays: number[]): string {
  return [...weekdays].sort((a, b) => a - b).map(d => WEEKDAY_LABELS[d] || '?').join(', ');
}

function formatTime(start: string, end: string): string {
  const isOvernight = end < start;
  return `${start} - ${end}${isOvernight ? ' (+1)' : ''}`;
}

function toShiftRuleInput(rule: ShiftRule): ShiftRuleInput {
  return {
    id: rule.id,
    code: rule.code,
    name: rule.name,
    shiftTypeId: rule.shiftType.id,
    shiftTypeCode: rule.shiftType.code,
    shiftTypeName: rule.shiftType.name,
    weekdays: rule.weekdays,
    startTime: rule.startTime,
    endTime: rule.endTime,
    validFrom: rule.validFrom ? new Date(rule.validFrom) : null,
    validUntil: rule.validUntil ? new Date(rule.validUntil) : null,
    priority: rule.priority,
  };
}

// =============================================================================
// Main Component
// =============================================================================

export default function ShiftsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Refs für Scroll-Targets
  const typesSectionRef = useRef<HTMLElement>(null);
  const rulesSectionRef = useRef<HTMLElement>(null);
  
  // Highlight State (für temporäres Highlighting eines Eintrags)
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Schichttypen State
  const [types, setTypes] = useState<ShiftType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [showInactiveTypes, setShowInactiveTypes] = useState(false);

  // Schichtregeln State
  const [rules, setRules] = useState<ShiftRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [showInactiveRules, setShowInactiveRules] = useState(false);

  // Current Shift Status
  const [currentShift, setCurrentShift] = useState<CurrentShiftResponse | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Schicht-Einstellungen State
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSuccess, setConfigSuccess] = useState<string | null>(null);
  const [boundaryGraceMinutes, setBoundaryGraceMinutes] = useState<number>(0);
  const [boundaryGraceInput, setBoundaryGraceInput] = useState<string>('0');

  // Preview Tool
  const [previewDate, setPreviewDate] = useState(() => {
    return formatBerlinDateISO(new Date());
  });
  const [previewTime, setPreviewTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [previewResult, setPreviewResult] = useState<{
    hasMatch: boolean;
    ruleName?: string;
    shiftTypeName?: string;
    shiftDateStr?: string;
    isOvernight?: boolean;
    explanation?: string;
    role?: 'base' | 'override';
    winReason?: string;
  } | null>(null);

  // Load Schichttypen
  const loadTypes = async () => {
    setTypesLoading(true);
    try {
      const res = await fetch('/api/admin/shift-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTypes(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichttypen:', err);
    }
    setTypesLoading(false);
  };

  // Load Schichtregeln
  const loadRules = async () => {
    setRulesLoading(true);
    try {
      const res = await fetch('/api/admin/shift-rules', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRules(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichtregeln:', err);
    }
    setRulesLoading(false);
  };

  // Check current shift status via API
  const checkCurrentStatus = async () => {
    setCheckingStatus(true);
    try {
      const res = await fetch('/api/shifts/current', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCurrentShift(data);
      }
    } catch (err) {
      console.error('Fehler beim Prüfen der aktuellen Schicht:', err);
    }
    setCheckingStatus(false);
  };

  // Load Schicht-Konfiguration
  const loadConfig = async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const res = await fetch('/api/shifts/config', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBoundaryGraceMinutes(data.boundaryGraceMinutes);
        setBoundaryGraceInput(String(data.boundaryGraceMinutes));
      } else {
        const err = await res.json().catch(() => ({}));
        setConfigError(err.error || 'Fehler beim Laden');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Konfiguration:', err);
      setConfigError('Verbindungsfehler');
    }
    setConfigLoading(false);
  };

  // Save Schicht-Konfiguration
  const saveConfig = async () => {
    const parsed = parseInt(boundaryGraceInput, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 60) {
      setConfigError('Wert muss zwischen 0 und 60 liegen');
      return;
    }

    setConfigSaving(true);
    setConfigError(null);
    setConfigSuccess(null);
    try {
      const res = await fetch('/api/shifts/config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ boundaryGraceMinutes: parsed }),
      });
      if (res.ok) {
        const data = await res.json();
        setBoundaryGraceMinutes(data.boundaryGraceMinutes);
        setBoundaryGraceInput(String(data.boundaryGraceMinutes));
        setConfigSuccess('Einstellung gespeichert');
        setTimeout(() => setConfigSuccess(null), 3000);
      } else {
        const err = await res.json().catch(() => ({}));
        setConfigError(err.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Konfiguration:', err);
      setConfigError('Verbindungsfehler');
    }
    setConfigSaving(false);
  };

  // Input-Handler mit Validierung
  const handleGraceInputChange = (value: string) => {
    // Nur Ziffern erlauben
    const cleaned = value.replace(/[^0-9]/g, '');
    setBoundaryGraceInput(cleaned);
  };

  // Prüfen ob sich der Wert geändert hat
  const configIsDirty = parseInt(boundaryGraceInput, 10) !== boundaryGraceMinutes;

  // Run preview with selected date/time
  const runPreview = () => {
    if (!previewDate || !previewTime) return;

    const berlinDateStr = `${previewDate}T${previewTime}:00`;
    const timestamp = new Date(berlinDateStr);
    
    const activeRules = rules.filter(r => r.isActive).map(toShiftRuleInput);
    
    if (activeRules.length === 0) {
      setPreviewResult({
        hasMatch: false,
        explanation: 'Keine aktiven Regeln vorhanden.',
      });
      return;
    }

    const match = matchShiftRule(activeRules, timestamp);
    
    if (!match) {
      const weekday = timestamp.getDay();
      const isoWeekday = weekday === 0 ? 7 : weekday;
      setPreviewResult({
        hasMatch: false,
        explanation: `Keine Regel matcht für ${WEEKDAY_FULL[isoWeekday]} ${previewTime} Uhr. Prüfe Wochentage und Zeitfenster der aktiven Regeln.`,
      });
      return;
    }

    const isOvernight = match.rule.endTime < match.rule.startTime;
    const shiftDateStr = match.shiftDateStr;
    const shiftDateDifferent = shiftDateStr !== previewDate;
    
    let explanation = `Regel "${match.rule.name}" matcht.`;
    if (isOvernight && shiftDateDifferent) {
      explanation += ` Dies ist eine Overnight-Schicht. Das Schichtdatum ist ${shiftDateStr} (Vortag), da die Schicht um ${match.rule.startTime} begann.`;
    }

    // Bestimme Rolle und Gewinner-Grund
    const matchedRule = rules.find(r => r.id === match.rule.id);
    const role = matchedRule ? getRuleRole(matchedRule) : 'base';
    
    // Finde konkurrierende Regeln für Gewinner-Erklärung
    let winReason = '';
    const competingRules = activeRules.filter(r => r.id !== match.rule.id);
    if (competingRules.length > 0) {
      const maxCompetingPriority = Math.max(...competingRules.map(r => r.priority));
      if (match.rule.priority > maxCompetingPriority) {
        winReason = role === 'override' 
          ? 'Ausnahme mit höchster Priorität'
          : `Höchste Priorität (${match.rule.priority})`;
      } else if (match.rule.priority === maxCompetingPriority) {
        winReason = 'Gewinner durch alphabetische Code-Reihenfolge';
      } else {
        winReason = 'Einzige passende Regel für diesen Zeitpunkt';
      }
    } else {
      winReason = 'Einzige aktive Regel';
    }

    setPreviewResult({
      hasMatch: true,
      ruleName: match.rule.name,
      shiftTypeName: match.rule.shiftTypeName,
      shiftDateStr,
      isOvernight,
      explanation,
      role,
      winReason,
    });
  };

  useEffect(() => {
    loadTypes();
    loadRules();
    loadConfig();
  }, []);

  // Focus/Highlight nach Redirect von Detailseite
  useEffect(() => {
    const focus = searchParams.get('focus');
    const id = searchParams.get('id');
    
    if (!focus || !id) return;
    
    // Warten bis Daten geladen sind
    if ((focus === 'types' && typesLoading) || (focus === 'rules' && rulesLoading)) return;
    
    // Zur entsprechenden Sektion scrollen
    const targetRef = focus === 'types' ? typesSectionRef : rulesSectionRef;
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    // Eintrag highlighten
    setHighlightedId(id);
    
    // URL bereinigen (Query-Params entfernen)
    setSearchParams({}, { replace: true });
    
    // Highlight nach 2.5s entfernen
    const timer = setTimeout(() => {
      setHighlightedId(null);
    }, 2500);
    
    return () => clearTimeout(timer);
  }, [searchParams, typesLoading, rulesLoading, setSearchParams]);

  const filteredTypes = sortAlpha(showInactiveTypes ? types : types.filter(t => t.isActive));
  const activeTypesCount = types.filter(t => t.isActive).length;

  const filteredRules = showInactiveRules ? rules : rules.filter(r => r.isActive);
  const sortedRules = [...filteredRules].sort((a, b) => {
    if (a.priority !== b.priority) return b.priority - a.priority;
    return a.name.localeCompare(b.name, 'de');
  });
  const activeRulesCount = rules.filter(r => r.isActive).length;

  // Berechne Konflikte für jede Regel (vereinfachte Version)
  const ruleConflicts = useMemo(() => {
    const conflicts: Record<string, number> = {};
    const activeRules = rules.filter(r => r.isActive);
    
    for (const rule of activeRules) {
      let conflictCount = 0;
      for (const other of activeRules) {
        if (rule.id === other.id) continue;
        
        // Prüfe Wochentag-Überschneidung
        const weekdayOverlap = rule.weekdays.some(d => other.weekdays.includes(d));
        if (!weekdayOverlap) continue;
        
        // Prüfe Zeit-Überschneidung (vereinfacht)
        const parseTime = (t: string) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };
        const ruleStart = parseTime(rule.startTime);
        const ruleEnd = parseTime(rule.endTime);
        const otherStart = parseTime(other.startTime);
        const otherEnd = parseTime(other.endTime);
        
        const ruleIsOvernight = ruleEnd < ruleStart;
        const otherIsOvernight = otherEnd < otherStart;
        
        const ruleRanges = ruleIsOvernight
          ? [[ruleStart, 1440], [0, ruleEnd]]
          : [[ruleStart, ruleEnd]];
        const otherRanges = otherIsOvernight
          ? [[otherStart, 1440], [0, otherEnd]]
          : [[otherStart, otherEnd]];
        
        let timeOverlap = false;
        for (const [rs, re] of ruleRanges) {
          for (const [os, oe] of otherRanges) {
            if (rs < oe && re > os) {
              timeOverlap = true;
              break;
            }
          }
          if (timeOverlap) break;
        }
        
        if (timeOverlap) conflictCount++;
      }
      conflicts[rule.id] = conflictCount;
    }
    
    return conflicts;
  }, [rules]);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            Schichten
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {activeTypesCount} Schichttypen, {activeRulesCount} Schichtregeln
          </p>
        </div>

        {/* Status/Preview Section (oben) */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Current Shift Status */}
          <section className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Aktuelle Schicht
              </h2>
              <button
                onClick={checkCurrentStatus}
                disabled={checkingStatus}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
              >
                {checkingStatus ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Search className="w-3 h-3" />
                )}
                Prüfen
              </button>
            </div>
            
            {currentShift === null ? (
              <p className="text-sm text-slate-500">Klicke "Prüfen" um den aktuellen Status zu sehen.</p>
            ) : currentShift.hasActiveShift ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700">Erfassung möglich</span>
                </div>
                <div className="text-sm text-slate-600 space-y-1">
                  <p><strong>Regel:</strong> {currentShift.ruleName}</p>
                  <p><strong>Typ:</strong> {currentShift.shiftTypeName}</p>
                  <p><strong>Zeitfenster:</strong> {currentShift.startTime} - {currentShift.endTime}</p>
                  <p><strong>Schichtdatum:</strong> {currentShift.shiftDateStr}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <span className="font-medium text-red-700">Keine aktive Schicht</span>
                </div>
                <p className="text-sm text-slate-600">
                  Erfassung ist aktuell <strong>nicht möglich</strong>. 
                </p>
              </div>
            )}
          </section>

          {/* Preview Tool */}
          <section className="bg-white rounded-lg border p-4">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5" />
              Vorschau (Datum/Uhrzeit testen)
            </h2>
            
            <div className="flex flex-wrap gap-2 mb-3">
              <input
                type="date"
                value={previewDate}
                onChange={(e) => setPreviewDate(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
              />
              <input
                type="time"
                value={previewTime}
                onChange={(e) => setPreviewTime(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
              />
              <button
                onClick={runPreview}
                disabled={!previewDate || !previewTime || rules.length === 0}
                className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1"
              >
                <Search className="w-3 h-3" />
                Vorschau
              </button>
            </div>

            {previewResult === null ? (
              <p className="text-sm text-slate-500">Wähle Datum und Uhrzeit (Berlin) und klicke "Vorschau".</p>
            ) : previewResult.hasMatch ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700">Regel matcht</span>
                </div>
                <div className="text-sm text-slate-600 space-y-1">
                  <div className="flex items-center gap-2">
                    <strong>Regel:</strong> {previewResult.ruleName}
                    {previewResult.role && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        previewResult.role === 'override'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {previewResult.role === 'override' ? 'Ausnahme' : 'Basis'}
                      </span>
                    )}
                  </div>
                  <p><strong>Typ:</strong> {previewResult.shiftTypeName}</p>
                  <p><strong>Schichtdatum:</strong> {previewResult.shiftDateStr}</p>
                  {previewResult.isOvernight && (
                    <p className="text-amber-700 bg-amber-50 px-2 py-1 rounded">
                      <Info className="w-3 h-3 inline mr-1" />
                      Overnight-Schicht
                    </p>
                  )}
                  {previewResult.winReason && (
                    <p className="text-green-700 bg-green-50 px-2 py-1 rounded text-xs mt-2">
                      <strong>Warum?</strong> {previewResult.winReason}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <span className="font-medium text-red-700">Keine Regel matcht</span>
                </div>
                <p className="text-sm text-slate-500">{previewResult.explanation}</p>
              </div>
            )}
          </section>
        </div>

        {/* Hinweis-Banner bei inaktiver Schicht */}
        {currentShift && !currentShift.hasActiveShift && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Hinweis zur Erfassung</p>
              <p className="mt-1">
                Ohne aktive Schichtregel können keine Erfassungen zugeordnet werden. 
                Prüfen Sie die Schichtregeln oder erweitern Sie die Zeitfenster.
              </p>
            </div>
          </div>
        )}

        {/* Section: Einstellungen */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <h2 className="text-lg font-semibold text-slate-900">Einstellungen</h2>
          </div>

          {configLoading ? (
            <div className="text-center py-4 text-slate-500">Lade Einstellungen...</div>
          ) : (
            <div className="space-y-4">
              {/* Feedback */}
              {configError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {configError}
                </div>
              )}
              {configSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {configSuccess}
                </div>
              )}

              {/* Kulanz an Schichtgrenzen */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <label htmlFor="boundaryGrace" className="block text-sm font-medium text-slate-700">
                    Kulanz an Schichtgrenzen (Minuten)
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Einträge kurz nach Schichtwechsel werden der vorherigen Schicht zugerechnet. Gilt für alle Schichtgrenzen.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="boundaryGrace"
                    type="text"
                    inputMode="numeric"
                    value={boundaryGraceInput}
                    onChange={(e) => handleGraceInputChange(e.target.value)}
                    className="w-20 px-3 py-2 border border-slate-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                  <span className="text-sm text-slate-500">min</span>
                  <button
                    onClick={saveConfig}
                    disabled={configSaving || !configIsDirty}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {configSaving ? 'Speichern...' : 'Speichern'}
                  </button>
                </div>
              </div>

              {/* Info bei deaktiviert */}
              {boundaryGraceMinutes === 0 && !configIsDirty && (
                <p className="text-xs text-slate-400 italic">
                  Kulanzfenster ist deaktiviert (0 Minuten).
                </p>
              )}
            </div>
          )}
        </section>

        {/* Section: Schichttypen */}
        <section ref={typesSectionRef} className="bg-white rounded-lg border p-6 space-y-4 scroll-mt-4">
          <div className="flex items-center justify-between border-b pb-3">
            <h2 className="text-lg font-semibold text-slate-900">Schichttypen</h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showInactiveTypes}
                  onChange={(e) => setShowInactiveTypes(e.target.checked)}
                  className="rounded"
                />
                Inaktive
              </label>
              <button
                onClick={loadTypes}
                disabled={typesLoading}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${typesLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => navigate('/admin/shift-types/new')}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Neuer Typ
              </button>
            </div>
          </div>

          {typesLoading ? (
            <div className="text-center py-8 text-slate-500">Lade Schichttypen...</div>
          ) : filteredTypes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Keine Schichttypen vorhanden</div>
          ) : (
            <div className="divide-y -mx-6">
              {filteredTypes.map((type) => (
                <div
                  key={type.id}
                  onClick={() => navigate(`/admin/shift-types/${type.id}`)}
                  className={`flex items-center justify-between px-6 py-3 hover:bg-slate-50 cursor-pointer transition-all duration-300 ${
                    highlightedId === type.id ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''
                  }`}
                >
                  <span className="font-medium text-slate-900">{type.name}</span>
                  <div className="flex items-center gap-3">
                    {type.isActive ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                        <CheckCircle className="w-3 h-3" /> Aktiv
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        <XCircle className="w-3 h-3" /> Inaktiv
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Section: Schichtregeln */}
        <section ref={rulesSectionRef} className="bg-white rounded-lg border p-6 space-y-4 scroll-mt-4">
          <div className="flex items-center justify-between border-b pb-3">
            <h2 className="text-lg font-semibold text-slate-900">Schichtregeln</h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showInactiveRules}
                  onChange={(e) => setShowInactiveRules(e.target.checked)}
                  className="rounded"
                />
                Inaktive
              </label>
              <button
                onClick={loadRules}
                disabled={rulesLoading}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${rulesLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => navigate('/admin/shift-rules/new')}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Neue Regel
              </button>
            </div>
          </div>

          {rulesLoading ? (
            <div className="text-center py-8 text-slate-500">Lade Schichtregeln...</div>
          ) : sortedRules.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Keine Schichtregeln vorhanden</div>
          ) : (
            <div className="divide-y -mx-6">
              {sortedRules.map((rule) => {
                const role = getRuleRole(rule);
                const conflictCount = ruleConflicts[rule.id] || 0;
                const validityStr = formatValidity(rule.validFrom, rule.validUntil);
                
                return (
                  <div
                    key={rule.id}
                    onClick={() => navigate(`/admin/shift-rules/${rule.id}`)}
                    className={`flex items-center justify-between px-6 py-3 hover:bg-slate-50 cursor-pointer transition-all duration-300 ${
                      highlightedId === rule.id ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{rule.name}</span>
                        {/* Rolle-Chip */}
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                          role === 'override'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {role === 'override' ? (
                            <><Shield className="w-3 h-3" /> Ausnahme</>
                          ) : (
                            <><Layers className="w-3 h-3" /> Basis</>
                          )}
                        </span>
                        {/* Konflikt-Badge */}
                        {conflictCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                            <AlertTriangle className="w-3 h-3" /> {conflictCount} Konflikt{conflictCount > 1 ? 'e' : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                        <span>{rule.shiftType.name}</span>
                        <span>·</span>
                        <span>{formatWeekdays(rule.weekdays)}</span>
                        <span>·</span>
                        <span>{formatTime(rule.startTime, rule.endTime)}</span>
                        <span>·</span>
                        <span className={validityStr === 'unbegrenzt' ? 'text-slate-400' : 'text-slate-600'}>
                          {validityStr}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {rule.isActive ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                          <CheckCircle className="w-3 h-3" /> Aktiv
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                          <XCircle className="w-3 h-3" /> Inaktiv
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
